/**
 * Identity of the signed-in user, read from local storage.
 *
 * Sign-in writes `logged_in_qram` as `{ token, user }`. Both HTTP transports
 * need the username to send the `X-User` header, so the parsing lives here
 * rather than being repeated at every call site.
 */

const STORAGE_KEY = 'logged_in_qram';

/**
 * Header field values may only carry visible ASCII (RFC 7230). A username with
 * an accent, a tab or a non-Latin character makes `setRequestHeader()` throw,
 * which fails the entire request instead of merely dropping the header, so
 * anything outside that range is reported as unusable.
 *
 * @type {RegExp}
 */
const HEADER_SAFE = /^[\x20-\x7e]+$/;

/**
 * Reads the username of the signed-in user.
 *
 * Returns null whenever the value cannot be sent safely: signed out, malformed
 * storage, or a username the HTTP layer would reject. Callers treat null as
 * "omit the header" rather than sending an empty value, because the backend
 * rate limiter has to tell "no user" apart from "user with a blank name".
 *
 * @returns {string|null} The username, or null when there is none to send.
 */
export const readUsername = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const username = JSON.parse(raw)?.user?.username;
    if (typeof username !== 'string') return null;

    const trimmed = username.trim();
    return trimmed !== '' && HEADER_SAFE.test(trimmed) ? trimmed : null;
  } catch (error) {
    return null;
  }
};

export default readUsername;
